import { Button, Input, Modal, Table } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import DatePickerComponent from "../../Common/Components/DatePickerComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { PermissionOperations } from "~enums/permissions";
import { useNotification } from "~hooks/useNotification";

const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

export const AddHistoricNameModal = ({
                                       defaultName = "",
                                       defaultStartDate = null,
                                       defaultEndDate = null,
                                       isAddModalOpen,
                                       setIsAddModalOpen,
                                       isEditMode = false,
                                       handleUpdate,
                                       modalTitle,
                                     }) => {
  const { formatMessage } = useIntl();

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const notification = useNotification();

  useEffect(() => {
    setName(defaultName);
    if (defaultStartDate) {
      setStartDate(defaultStartDate);
    }
    if (defaultEndDate) {
      setEndDate(defaultEndDate);
    }
  }, [defaultName, defaultStartDate, defaultEndDate]);

  const handleOk = useCallback(async () => {
    if(!startDate || !endDate){
      if(!startDate){
        notification.error({
          message: formatMessage({ id: "date.error.start.not.specified" }),
          placement: "bottomRight",
        });
      }else{
        notification.error({
          message: formatMessage({ id: "date.error.end.not.specified" }),
          placement: "bottomRight",
        });
      }
      return;
    }
    if (endDate.isBefore(startDate)) {
      notification.error({
        message: formatMessage({ id: "date.error.endShouldBeAfterStart" }),
        placement: "bottomRight",
      });
      return;
    }
    if (!name) {
      notification.error({
        message: formatMessage({ id: "name.is.required" }),
        placement: "bottomRight",
      })
      return;
    }
    await handleUpdate({ nameFullLocal: name, dateStart: startDate, dateEnd: endDate }, isEditMode, {
      defaultName,
      defaultStartDate,
      defaultEndDate,
    });
    setIsAddModalOpen(false);
  }, [
    endDate,
    startDate,
    name,
    handleUpdate,
    isEditMode,
    defaultName,
    defaultStartDate,
    defaultEndDate,
    setIsAddModalOpen,
    formatMessage,
  ]);

  return (
    <>
      <Modal
        open={isAddModalOpen}
        onCancel={() => {
          setIsAddModalOpen(false);
        }}
        onOk={handleOk}
      >
        <ModalBody>
          <label htmlFor={"historicalName"}>
            {formatMessage({
              id: modalTitle ? modalTitle : "name",
            })}
          </label>
          <Input
            id={"historicalName"}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <label htmlFor={"historicalNameStartDate"}>{formatMessage({ id: "date.start" })}: </label>
          <DatePickerComponent
            id={"historicalNameStartDate"}
            defaultValue={startDate}
            handleChange={(value) => {
              setStartDate(value);
            }}
          />
          <label htmlFor={"historicalNameEndDate"}>{formatMessage({ id: "date.end" })}: </label>
          <DatePickerComponent
            id={"historicalNameEndDate"}
            defaultValue={endDate}
            handleChange={(value) => {
              setEndDate(value);
            }}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

const HistoricNames = ({ historicalNames, columns, handleUpdate, modalTitle, permissionName }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { formatMessage } = useIntl();

  return (
    <>
      <PermissionCheck permissionKeys={[permissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
        <div className="historical-name-add-button">
          <AddHistoricNameModal
            historicalNames={historicalNames}
            isAddModalOpen={isAddModalOpen}
            setIsAddModalOpen={setIsAddModalOpen}
            handleUpdate={handleUpdate}
            modalTitle={modalTitle}
          />
          <Button
            type="primary"
            onClick={() => {
              setIsAddModalOpen(true);
            }}
          >
            {formatMessage({ id: "historicalName.add" })}
          </Button>
        </div>
      </PermissionCheck>
      <Table size="small" columns={columns} dataSource={historicalNames || []}/>
    </>
  );
};

export default HistoricNames;
